export type TabBarPlatform = 'desktop' | 'mobile' | 'application';
export type TabBarDestination = 'gallery' | 'bazaar' | 'used';
export const TAB_BAR_PLATFORMS: readonly TabBarPlatform[] = ['desktop', 'mobile', 'application'];
// Read older configurations with added links so the three original tabs retain their appearance.
const LEGACY_MAX_TAB_BAR_TABS = 12;

export interface TabBarItem {
  id: string;
  destination: TabBarDestination;
  nameEn: string;
  nameAr: string;
  active: boolean;
  iconEn: string | null;
  iconAr: string | null;
  gradient1: string;
  gradient2: string;
  activeBackground: string;
  inactiveBackground: string;
  /** Older saved configurations omit this field and use white. */
  pageBackground?: string;
}

export interface TabBarConfig {
  version: 1;
  tabs: TabBarItem[];
  sectionStatus?: { bazaar: boolean; used: boolean };
}

export type TabBarSettings = Record<TabBarPlatform, TabBarConfig | null>;

/** Only the three existing storefront sections can be configured. */
export function isBuiltInTab(tab: Pick<TabBarItem, 'id' | 'destination'>): boolean {
  return ['gallery', 'bazaar', 'used'].includes(tab.id) && tab.id === tab.destination;
}

export function tabBarSettingKey(platform: TabBarPlatform): string {
  return `storefront_tab_bar_${platform}_v1`;
}

export function emptyTabBarSettings(): TabBarSettings {
  return { desktop: null, mobile: null, application: null };
}

export function isTabBarIconUrl(value: unknown): value is string {
  if (typeof value !== 'string' || value !== value.trim()) return false;
  if (/^\/assets\/[a-zA-Z0-9_./-]+$/.test(value)) return true;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' || url.protocol === 'http:';
  } catch {
    return false;
  }
}

export function parseTabBarConfig(value: unknown): TabBarConfig {
  if (!value || typeof value !== 'object') throw new Error('Invalid tab bar settings.');
  const config = value as Record<string, unknown>;
  if (config['version'] !== 1 || !Array.isArray(config['tabs']) || config['tabs'].length > LEGACY_MAX_TAB_BAR_TABS) {
    throw new Error('Unsupported tab bar settings.');
  }
  const ids = new Set<string>();
  const tabs = config['tabs'].map((value: unknown): TabBarItem => {
    if (!value || typeof value !== 'object') throw new Error('Invalid tab bar section.');
    const tab = value as Record<string, unknown>;
    if (typeof tab['id'] !== 'string' || !/^[a-zA-Z0-9_-]{1,80}$/.test(tab['id']) || ids.has(tab['id'])) {
      throw new Error('Each tab bar section must have a unique ID.');
    }
    ids.add(tab['id']);
    if (!['gallery', 'bazaar', 'used'].includes(String(tab['destination'])) || typeof tab['active'] !== 'boolean') {
      throw new Error('Invalid tab destination or status.');
    }
    for (const key of ['nameEn', 'nameAr']) {
      const name = tab[key];
      if (typeof name !== 'string' || !name.trim() || name !== name.trim() || name.length > 50) {
        throw new Error('Section names must contain 1–50 characters.');
      }
    }
    for (const key of ['gradient1', 'gradient2', 'activeBackground', 'inactiveBackground']) {
      if (typeof tab[key] !== 'string' || !/^#[0-9a-fA-F]{6}$/.test(tab[key])) throw new Error('Invalid section colour.');
    }
    const pageBackground = tab['pageBackground'] ?? '#FFFFFF';
    if (typeof pageBackground !== 'string' || !/^#[0-9a-fA-F]{6}$/.test(pageBackground)) {
      throw new Error('Invalid page background colour.');
    }
    for (const key of ['iconEn', 'iconAr']) {
      if (tab[key] !== null && !isTabBarIconUrl(tab[key])) throw new Error('Upload section icons before saving.');
    }
    return {
      id: tab['id'],
      destination: tab['destination'] as TabBarDestination,
      nameEn: tab['nameEn'] as string,
      nameAr: tab['nameAr'] as string,
      active: tab['active'],
      iconEn: tab['iconEn'] as string | null,
      iconAr: tab['iconAr'] as string | null,
      gradient1: tab['gradient1'] as string,
      gradient2: tab['gradient2'] as string,
      activeBackground: tab['activeBackground'] as string,
      inactiveBackground: tab['inactiveBackground'] as string,
      pageBackground
    };
  });
  const status = config['sectionStatus'];
  if (status !== undefined) {
    if (!status || typeof status !== 'object' || typeof status['bazaar'] !== 'boolean' || typeof status['used'] !== 'boolean') {
      throw new Error('Invalid section status.');
    }
    return withBuiltInTabs({ version: 1, tabs, sectionStatus: { bazaar: status['bazaar'], used: status['used'] } });
  }
  return withBuiltInTabs({ version: 1, tabs });
}

/** Discard retired custom links, preserving original tab order and appearance. */
export function withBuiltInTabs(config: TabBarConfig): TabBarConfig {
  const defaults = defaultTabBarConfig().tabs;
  const legacyIcons = [
    '/assets/used-tech/section-star.png',
    '/assets/storefront-header/gallery-wordmark.png',
    '/assets/storefront-header/used-wordmark.png',
    '/assets/storefront-header/bazaar-wordmark.png'
  ];
  const tabs: TabBarItem[] = config.tabs.filter(isBuiltInTab).map(tab => {
    const fallback = defaults.find(item => item.id === tab.id)!;
    return {
      ...tab,
      pageBackground: tab.pageBackground ?? '#FFFFFF',
      nameEn: /[\p{L}\p{N}]/u.test(tab.nameEn) ? tab.nameEn : fallback.nameEn,
      nameAr: /[\p{L}\p{N}]/u.test(tab.nameAr) ? tab.nameAr : fallback.nameAr,
      active: tab.id === 'gallery' ? true : tab.active,
      iconEn: legacyIcons.includes(tab.iconEn ?? '') ? fallback.iconEn : tab.iconEn,
      iconAr: legacyIcons.includes(tab.iconAr ?? '') ? fallback.iconAr : tab.iconAr
    };
  });
  for (const tab of defaults) {
    if (tabs.some(item => item.id === tab.id)) continue;
    if (tab.destination === 'gallery') tabs.unshift(tab);
    else tabs.push({ ...tab, active: config.sectionStatus?.[tab.destination] ?? tab.active });
  }
  return { ...config, tabs };
}

export function defaultTabBarConfig(): TabBarConfig {
  const common = { active: true, iconAr: null, iconEn: null, activeBackground: '#003B5C', inactiveBackground: '#FFFFFF', pageBackground: '#FFFFFF' };
  return {
    version: 1,
    tabs: [
      {
        ...common,
        id: 'gallery',
        destination: 'gallery',
        nameEn: 'Star Gallery',
        nameAr: 'ستار جاليري',
        iconEn: '/assets/logo-en.svg',
        iconAr: '/assets/logo-ar.svg',
        gradient1: '#0DAC57',
        gradient2: '#DA1725'
      },
      {
        ...common,
        id: 'bazaar',
        destination: 'bazaar',
        nameEn: 'Bazaar',
        nameAr: 'بازار',
        iconEn: '/assets/used-tech/section-deals.png',
        iconAr: '/assets/used-tech/section-deals.png',
        gradient1: '#E53621',
        gradient2: '#A31C0C'
      },
      {
        ...common,
        id: 'used',
        destination: 'used',
        nameEn: 'Used Tech',
        nameAr: 'التكنولوجيا المستعملة',
        iconEn: '/assets/used-tech/section-phone.png',
        iconAr: '/assets/used-tech/section-phone.png',
        gradient1: '#FEEE00',
        gradient2: '#FFF670'
      }
    ]
  };
}

/** Choose readable text for an administrator-selected solid button background. */
export function tabBarTextColour(hex: string): string {
  const channel = (start: number) => {
    const v = parseInt(hex.slice(start, start + 2), 16) / 255;
    return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  const luminance = 0.2126 * channel(1) + 0.7152 * channel(3) + 0.0722 * channel(5);
  return luminance > 0.179 ? '#000000' : '#FFFFFF';
}
